import { createPortal, memo } from "preact/compat";
import {
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import { Crosshair, Maximize, Minus, Plus, X } from "lucide-react";
import type { MapDocument, MapLocation } from "../shared/types";
import type { VenueMapLogos } from "../map/useVenueMapLogos";
import { VenueMapScene } from "../map/VenueMapScene";
import "./location-map-editor.css";

type Point = { x: number; y: number };
type View = { zoom: number; centerX: number; centerY: number };
type SingleGesture = {
  kind: "marker" | "pan";
  pointerId: number;
  startClient: Point;
  startOuter: Point;
  startSource: Point;
  startPosition: Point;
  startView: View;
  moved: boolean;
};
type Gesture =
  | SingleGesture
  | {
      kind: "pinch";
      distance: number;
      zoom: number;
      anchor: Point;
    };

export interface LocationMapEditorProps {
  document: MapDocument;
  location: MapLocation;
  logos?: VenueMapLogos;
  onApply: (position: Point) => void;
  onClose: () => void;
  booth?: boolean;
  /** Explicit focus destination when this dialog follows another modal. */
  returnFocusTo?: () => HTMLElement | null;
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));
const coordinate = (value: number) =>
  clamp(Number.isFinite(value) ? value : 0, 0, 1);
const percentage = (value: number) => Number((value * 100).toFixed(2));
const midpoint = (a: Point, b: Point): Point => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
});

function constrainView(next: View, width: number, height: number): View {
  const zoom = clamp(next.zoom, 1, 8);
  return {
    zoom,
    centerX: clamp(
      next.centerX,
      width / (2 * zoom),
      width - width / (2 * zoom),
    ),
    centerY: clamp(
      next.centerY,
      height / (2 * zoom),
      height - height / (2 * zoom),
    ),
  };
}

function fittedView(geometry: MapDocument["geometry"], position: Point): View {
  const { width, height, floor } = geometry;
  // Imported drawings often have large canvas margins. Frame the floor while
  // keeping an outlying location in view; coordinates still use the full canvas.
  const left = Math.min(floor.x, position.x * width);
  const right = Math.max(floor.x + floor.width, position.x * width);
  const top = Math.min(floor.y, position.y * height);
  const bottom = Math.max(floor.y + floor.height, position.y * height);
  return constrainView(
    {
      zoom: Math.min(
        width / Math.max(1, (right - left) * 1.08),
        height / Math.max(1, (bottom - top) * 1.08),
      ),
      centerX: (left + right) / 2,
      centerY: (top + bottom) / 2,
    },
    width,
    height,
  );
}

// Geometry, lettering and decoded logos stay fixed while only the enclosing
// SVG transform and the draft target move. No renderer or asset loader is created.
const PositionMapScene = memo(function PositionMapScene({
  document,
  logos,
}: Pick<LocationMapEditorProps, "document" | "logos">) {
  const { width, height } = document.geometry;
  return (
    <VenueMapScene
      document={document}
      logos={logos}
      camera={{
        sourceWidth: width,
        sourceHeight: height,
        imageWidth: width,
        imageHeight: height,
        stageWidth: width,
        stageHeight: height,
        rotation: 0,
        pitch: 0,
        perspective: false,
      }}
      viewportBounds={{ x: 0, y: 0, width, height }}
      description="Top-down venue map"
    />
  );
});

export function LocationMapEditor({
  document,
  location,
  logos,
  onApply,
  onClose,
  booth = false,
  returnFocusTo,
}: LocationMapEditorProps) {
  const { width, height } = document.geometry;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const transformRef = useRef<SVGGElement>(null);
  const markerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const focusReturnRef = useRef(returnFocusTo);
  focusReturnRef.current = returnFocusTo;
  const closing = useRef(false);
  const hintId = useId();
  const coordinatesId = useId();
  const [position, setPosition] = useState<Point>(() => ({
    x: coordinate(location.x),
    y: coordinate(location.y),
  }));
  const positionRef = useRef(position);
  const [view, setView] = useState<View>(() =>
    fittedView(document.geometry, position),
  );
  const viewRef = useRef(view);
  const [dragging, setDragging] = useState(false);
  const [markerScreen, setMarkerScreen] = useState<Point>({ x: 0, y: 0 });
  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef<Gesture | null>(null);
  const sceneDocument = useMemo<MapDocument>(() => {
    const hiddenLocations = new Set(
      document.locations.filter((item) => item.hidden).map((item) => item.id),
    );
    return {
      ...document,
      geometry: {
        ...document.geometry,
        stands: document.geometry.stands.map((stand) => ({
          ...stand,
          height: 0,
          // An edited hidden booth remains a useful positioning reference.
          hidden: stand.id === location.id ? false : stand.hidden,
        })),
        entrances: document.geometry.entrances.filter(
          (entrance) =>
            entrance.id !== location.id && !hiddenLocations.has(entrance.id),
        ),
      },
    };
  }, [document, location.id]);

  const requestClose = () => {
    if (closing.current) return;
    closing.current = true;
    closeRef.current();
  };
  const updatePosition = (next: Point) => {
    const normalized = { x: coordinate(next.x), y: coordinate(next.y) };
    positionRef.current = normalized;
    setPosition(normalized);
  };
  const updateView = (next: View) => {
    const constrained = constrainView(next, width, height);
    viewRef.current = constrained;
    setView(constrained);
  };

  // The outer SVG matrix includes responsive sizing and letterboxing. Invert
  // our camera separately using the latest ref, even before Preact has painted.
  const outerPoint = (client: Point): Point | null => {
    const matrix = svgRef.current?.getScreenCTM();
    if (!matrix) return null;
    return new DOMPoint(client.x, client.y).matrixTransform(matrix.inverse());
  };
  const sourcePoint = (client: Point): Point | null => {
    const point = outerPoint(client);
    if (!point) return null;
    const current = viewRef.current;
    return {
      x: current.centerX + (point.x - width / 2) / current.zoom,
      y: current.centerY + (point.y - height / 2) / current.zoom,
    };
  };
  const zoomTo = (zoom: number, client?: Point) => {
    const current = viewRef.current;
    const nextZoom = clamp(zoom, 1, 8);
    const outer = client ? outerPoint(client) : null;
    const anchor = client ? sourcePoint(client) : null;
    updateView({
      zoom: nextZoom,
      centerX:
        anchor && outer
          ? anchor.x - (outer.x - width / 2) / nextZoom
          : current.centerX,
      centerY:
        anchor && outer
          ? anchor.y - (outer.y - height / 2) / nextZoom
          : current.centerY,
    });
  };

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const page = globalThis.document;
    const opener = page.activeElement;
    const overflow = page.body.style.overflow;
    page.body.style.overflow = "hidden";
    dialog.showModal();
    markerRef.current?.focus({ preventScroll: true });
    return () => {
      closing.current = true;
      dialog.close();
      page.body.style.overflow = overflow;
      // Wait until the parent's update has completed before restoring focus.
      queueMicrotask(() => {
        const target = focusReturnRef.current?.() ?? opener;
        if (target instanceof HTMLElement && target.isConnected) {
          target.focus({ preventScroll: true });
        }
      });
    };
  }, []);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const measure = () => {
      const matrix = transformRef.current?.getScreenCTM();
      if (!matrix) return;
      const point = new DOMPoint(
        position.x * width,
        position.y * height,
      ).matrixTransform(matrix);
      const bounds = stage.getBoundingClientRect();
      setMarkerScreen({ x: point.x - bounds.left, y: point.y - bounds.top });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [position, view, width, height]);

  const beginSingle = (
    pointerId: number,
    client: Point,
    kind: SingleGesture["kind"],
    moved = false,
  ) => {
    const outer = outerPoint(client);
    const source = sourcePoint(client);
    if (!outer || !source) return;
    gesture.current = {
      kind,
      pointerId,
      startClient: client,
      startOuter: outer,
      startSource: source,
      startPosition: positionRef.current,
      startView: viewRef.current,
      moved,
    };
  };
  const beginPinch = () => {
    const [a, b] = [...pointers.current.values()];
    if (!a || !b) return;
    const anchor = sourcePoint(midpoint(a, b));
    if (!anchor) return;
    gesture.current = {
      kind: "pinch",
      distance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
      zoom: viewRef.current.zoom,
      anchor,
    };
    setDragging(true);
  };
  const pointerDown = (event: PointerEvent) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    const client = { x: event.clientX, y: event.clientY };
    pointers.current.set(event.pointerId, client);
    stageRef.current?.setPointerCapture(event.pointerId);
    if (pointers.current.size > 1) {
      beginPinch();
      return;
    }
    const onMarker =
      event.target instanceof Element &&
      !!event.target.closest(".em-location-position-marker");
    if (onMarker) markerRef.current?.focus({ preventScroll: true });
    beginSingle(event.pointerId, client, onMarker ? "marker" : "pan");
  };
  const pointerMove = (event: PointerEvent) => {
    if (!pointers.current.has(event.pointerId)) return;
    const client = { x: event.clientX, y: event.clientY };
    pointers.current.set(event.pointerId, client);
    const active = gesture.current;
    if (!active) return;
    if (active.kind === "pinch") {
      const [a, b] = [...pointers.current.values()];
      if (!a || !b) return;
      const outer = outerPoint(midpoint(a, b));
      if (!outer) return;
      const zoom = clamp(
        (active.zoom * Math.hypot(a.x - b.x, a.y - b.y)) / active.distance,
        1,
        8,
      );
      updateView({
        zoom,
        centerX: active.anchor.x - (outer.x - width / 2) / zoom,
        centerY: active.anchor.y - (outer.y - height / 2) / zoom,
      });
      return;
    }
    if (active.pointerId !== event.pointerId) return;
    if (
      Math.hypot(
        client.x - active.startClient.x,
        client.y - active.startClient.y,
      ) > 5
    ) {
      active.moved = true;
      setDragging(true);
    }
    if (!active.moved) return;
    if (active.kind === "marker") {
      const source = sourcePoint(client);
      if (!source) return;
      updatePosition({
        x: active.startPosition.x + (source.x - active.startSource.x) / width,
        y: active.startPosition.y + (source.y - active.startSource.y) / height,
      });
    } else {
      const outer = outerPoint(client);
      if (!outer) return;
      updateView({
        ...active.startView,
        centerX:
          active.startView.centerX -
          (outer.x - active.startOuter.x) / active.startView.zoom,
        centerY:
          active.startView.centerY -
          (outer.y - active.startOuter.y) / active.startView.zoom,
      });
    }
  };
  const finishPointer = (event: PointerEvent, cancelled = false) => {
    if (!pointers.current.has(event.pointerId)) return;
    if (!cancelled) pointerMove(event);
    const active = gesture.current;
    if (!cancelled && active?.kind === "pan" && !active.moved) {
      const source = sourcePoint({ x: event.clientX, y: event.clientY });
      if (source) updatePosition({ x: source.x / width, y: source.y / height });
    }
    pointers.current.delete(event.pointerId);
    if (stageRef.current?.hasPointerCapture(event.pointerId)) {
      stageRef.current.releasePointerCapture(event.pointerId);
    }
    if (pointers.current.size > 1) beginPinch();
    else if (pointers.current.size === 1) {
      const [pointerId, client] = [...pointers.current.entries()][0];
      // A finger left after a pinch may pan, but can never place a marker.
      beginSingle(pointerId, client, "pan", true);
    } else {
      gesture.current = null;
      setDragging(false);
    }
  };
  const markerKeyDown = (event: KeyboardEvent) => {
    const step = event.shiftKey ? 0.01 : 0.001;
    const movement: Record<string, Point> = {
      ArrowLeft: { x: -step, y: 0 },
      ArrowRight: { x: step, y: 0 },
      ArrowUp: { x: 0, y: -step },
      ArrowDown: { x: 0, y: step },
    };
    const delta = movement[event.key];
    if (!delta) return;
    event.preventDefault();
    event.stopPropagation();
    updatePosition({
      x: positionRef.current.x + delta.x,
      y: positionRef.current.y + delta.y,
    });
  };

  return createPortal(
    <dialog
      ref={dialogRef}
      className="em-location-position-dialog"
      aria-label="Position on map"
      aria-describedby={hintId}
      onCancel={(event) => {
        event.preventDefault();
        requestClose();
      }}
      onClose={requestClose}
      onKeyDown={(event) => {
        if (
          (event.metaKey || event.ctrlKey) &&
          event.key.toLowerCase() === "s"
        ) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      <div className="em-location-position-content">
        <header className="em-location-position-header">
          <div>
            <h2>Position on map</h2>
            <p>{location.label || location.id}</p>
          </div>
          <button
            type="button"
            className="em-icon-button"
            aria-label="Close position editor"
            onClick={requestClose}
          >
            <X size={19} aria-hidden="true" />
          </button>
        </header>
        <p id={hintId} className="em-location-position-help">
          Click or tap to place the marker. Drag the marker to adjust it, or
          drag the map to pan.
        </p>
        <div className="em-location-position-toolbar">
          <span>
            <Crosshair size={15} aria-hidden="true" /> Top-down view
          </span>
          <div>
            <button
              type="button"
              className="em-icon-button"
              aria-label="Zoom out"
              disabled={view.zoom <= 1}
              onClick={() => zoomTo(viewRef.current.zoom / 1.5)}
            >
              <Minus size={17} aria-hidden="true" />
            </button>
            <span className="em-location-position-zoom">
              {Math.round(view.zoom * 100)}%
            </span>
            <button
              type="button"
              className="em-icon-button"
              aria-label="Zoom in"
              disabled={view.zoom >= 8}
              onClick={() => zoomTo(viewRef.current.zoom * 1.5)}
            >
              <Plus size={17} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="em-button em-button-secondary"
              aria-label="Fit map"
              onClick={() =>
                updateView(fittedView(document.geometry, positionRef.current))
              }
            >
              <Maximize size={15} aria-hidden="true" /> Fit map
            </button>
          </div>
        </div>
        <div
          ref={stageRef}
          className="em-location-position-map"
          data-testid="location-position-map"
          data-dragging={dragging || undefined}
          onPointerDown={pointerDown}
          onPointerMove={pointerMove}
          onPointerUp={(event) => finishPointer(event)}
          onPointerCancel={(event) => finishPointer(event, true)}
          onLostPointerCapture={(event) => finishPointer(event, true)}
          onWheel={(event) => {
            event.preventDefault();
            // Keep one camera gesture in charge until its pointers release.
            if (gesture.current) return;
            const delta =
              event.deltaY *
              (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 300 : 1);
            zoomTo(viewRef.current.zoom * Math.exp(-delta * 0.002), {
              x: event.clientX,
              y: event.clientY,
            });
          }}
        >
          <svg
            ref={svgRef}
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
            focusable="false"
          >
            <g
              ref={transformRef}
              data-testid="location-position-transform"
              transform={`translate(${width / 2} ${height / 2}) scale(${view.zoom}) translate(${-view.centerX} ${-view.centerY})`}
            >
              <PositionMapScene document={sceneDocument} logos={logos} />
            </g>
          </svg>
          <button
            ref={markerRef}
            type="button"
            className="em-location-position-marker"
            data-testid="location-position-marker"
            aria-label="Location marker"
            aria-describedby={coordinatesId}
            title="Drag to move. Arrow keys adjust by 0.1%; hold Shift for 1%."
            style={{ left: markerScreen.x, top: markerScreen.y }}
            onKeyDown={markerKeyDown}
          >
            <span aria-hidden="true" />
          </button>
        </div>
        <div className="em-location-position-details">
          <output
            id={coordinatesId}
            data-testid="location-position-coordinates"
            aria-live="off"
          >
            X {percentage(position.x)}% <span>·</span> Y{" "}
            {percentage(position.y)}%
          </output>
          <p>Marker: arrow keys move 0.1%; Shift + arrow keys move 1%.</p>
          {booth && (
            <p>
              Sets the routing position. Visible booth pins stay anchored to
              their booth’s corner.
            </p>
          )}
        </div>
        <footer className="em-location-position-footer">
          <button
            type="button"
            className="em-button em-button-secondary"
            onClick={requestClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="em-button"
            onClick={() => {
              onApply(positionRef.current);
              requestClose();
            }}
          >
            Use position
          </button>
        </footer>
      </div>
    </dialog>,
    globalThis.document.body,
  );
}
